import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Alert, Button, ControlLabel, FormControl, FormGroup, Modal } from 'react-bootstrap';
import MaskedInput from 'react-maskedinput';
import StarRating from './star-rating';

interface IValidation {
    completedDate: boolean,
    treatment: boolean,
    convenience: boolean,
    facility: boolean,
    commendation: boolean,
    improvement: boolean,
    isValid: boolean
}

interface IProps {
    show: boolean,
    container: any,
    onSubmit: (treatment: number, convenience: number, facility: number, commendation: string, improvement: string, completedDate: string) => void,
    onCancel: () => void
}

interface IState {
    completedDate: string,
    treatment: number,
    convenience: number,
    facility: number,
    commendation: string,
    improvement: string,
    validation: IValidation,
    dirty: boolean
}

class PreOpSurvey extends React.Component<IProps, IState> {
    readonly state: IState = {
        completedDate: '',
        treatment: 0,
        convenience: 0,
        facility: 0,
        commendation: '',
        improvement: '',
        validation: {
            completedDate: true,
            treatment: true,
            convenience: true,
            facility: true,
            commendation: true,
            improvement: true,
            isValid: true
        },
        dirty: false
    }

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show) {
            this.setState({
                completedDate: '',
                treatment: 0,
                convenience: 0,
                facility: 0,
                commendation: '',
                improvement: '',
                validation: {
                    completedDate: true,
                    treatment: true,
                    convenience: true,
                    facility: true,
                    commendation: true,
                    improvement: true,
                    isValid: true
                },
                dirty: false
            });
        }
    }

    handleStarClick = (question: string, star: number) => {
        let validation = { ...this.state.validation };

        if (question == 'treatment') {
            if (this.state.dirty) {
                validation.treatment = star > 0;
                validation.isValid = this.isValid(validation);
            }

            this.setState({
                treatment: star,
                validation
            });
        }

        if (question == 'convenience') {
            if (this.state.dirty) {
                validation.convenience = star > 0;
                validation.isValid = this.isValid(validation);
            }

            this.setState({
                convenience: star,
                validation
            });
        }

        if (question == 'facility') {
            if (this.state.dirty) {
                validation.facility = star > 0;
                validation.isValid = this.isValid(validation);
            }

            this.setState({
                facility: star,
                validation
            });
        }
    }

    handleCommendationChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.commendation = e.currentTarget.value != '';
            validation.isValid = this.isValid(validation);
        }

        this.setState({
            commendation: e.currentTarget.value,
            validation
        });
    }

    handleImprovementChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.improvement = e.currentTarget.value != '';
            validation.isValid = this.isValid(validation);
        }

        this.setState({
            improvement: e.currentTarget.value,
            validation
        });
    }

    handleCompletedDateChange = (e: any) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.completedDate = e.currentTarget.value != '' && moment(e.currentTarget.value, 'MM/DD/YYYY', true).isValid();
            validation.isValid = this.isValid(validation);
        }

        this.setState({
            completedDate: e.currentTarget.value,
            validation
        });
    }

    handleSubmit = () => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.state.validation.isValid) {
                let { treatment, convenience, facility, commendation, improvement, completedDate } = this.state;
                this.props.onSubmit(treatment, convenience, facility, commendation, improvement, completedDate);
            }
        });
    }

    validate = (): IValidation => {
        let { treatment, convenience, facility, commendation, improvement, completedDate } = this.state;
        let isValid = treatment > 0 && convenience > 0 && facility > 0 && commendation != '' && improvement != '' && completedDate != '' && moment(completedDate, 'MM/DD/YYYY', true).isValid();

        return {
            completedDate: completedDate != '' && moment(completedDate, 'MM/DD/YYYY', true).isValid(),
            treatment: treatment > 0,
            convenience: convenience > 0,
            facility: facility > 0,
            commendation: commendation != '',
            improvement: improvement != '',
            isValid
        };
    }

    isValid = (validation: IValidation) => {
        let { treatment, convenience, facility, commendation, improvement, completedDate } = validation;
        let isValid = treatment && convenience && facility && commendation && improvement && completedDate && completedDate;
        return isValid;
    }

    render = () => {
        const treatmentInvalid = this.state.dirty && this.state.validation.treatment == false;
        const convenienceInvalid = this.state.dirty && this.state.validation.convenience == false;
        const facilityInvalid = this.state.dirty && this.state.validation.facility == false;

        return (
            <>
                <Modal backdrop='static' show={this.props.show} onHide={this.props.onCancel} container={this.props.container}>
                    <Modal.Header closeButton>
                        <Modal.Title>Pre-Op Survey</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {this.state.dirty && this.state.validation.isValid == false && (
                            <Alert bsStyle='danger' style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                                The items in red are required
                            </Alert>
                        )}

                        <div style={{ marginBottom: 10 }}>
                            On a scale of 1-5 (1 being the lowest score and 5 being the highest (best) score), please ask the donor to rate the following aspects of their pre-op experience...
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                            <StarRating
                                numberOfStars={5}
                                currentRating={this.state.treatment}
                                onClick={(rating) => this.handleStarClick('treatment', rating)}
                            />

                            <div style={{ marginLeft: 20, color: treatmentInvalid ? '#a94442' : '', fontWeight: treatmentInvalid ? 700 : 400 }}>How well were they treated?</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                            <StarRating
                                numberOfStars={5}
                                currentRating={this.state.convenience}
                                onClick={(rating) => this.handleStarClick('convenience', rating)}
                            />

                            <div style={{ marginLeft: 20, color: convenienceInvalid ? '#a94442' : '', fontWeight: convenienceInvalid ? 700 : 400 }}>How convenient was the workup process?</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                            <StarRating
                                numberOfStars={5}
                                currentRating={this.state.facility}
                                onClick={(rating) => this.handleStarClick('facility', rating)}
                            />

                            <div style={{ marginLeft: 20, color: facilityInvalid ? '#a94442' : '', fontWeight: facilityInvalid ? 700 : 400 }}>How would they rate the facility?</div>
                        </div>

                        <FormGroup validationState={this.state.dirty && this.state.validation.commendation == false ? 'error' : null}>
                            <ControlLabel>Was there anyone who did an especially great job taking care of them that they would like to commend?</ControlLabel>
                            <FormControl
                                componentClass='textarea'
                                style={{ height: 75, resize: 'none' }}
                                value={this.state.commendation}
                                onChange={this.handleCommendationChange}
                            />
                        </FormGroup>

                        <FormGroup validationState={this.state.dirty && this.state.validation.improvement == false ? 'error' : null}>
                            <ControlLabel>How can the transplant center improve?</ControlLabel>
                            <FormControl
                                componentClass='textarea'
                                style={{ height: 75, resize: 'none' }}
                                value={this.state.improvement}
                                onChange={this.handleImprovementChange}
                            />
                        </FormGroup>

                        <FormGroup style={{ marginBottom: 0 }} validationState={this.state.dirty && this.state.validation.completedDate == false ? 'error' : null}>
                            <ControlLabel>When was the workup completed?</ControlLabel>
                            <MaskedInput
                                className='form-control'
                                value={this.state.completedDate}
                                mask='11/11/1111'
                                onChange={this.handleCompletedDateChange}
                            />
                        </FormGroup>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button bsStyle='primary' onClick={this.handleSubmit}>Submit</Button>
                        <Button onClick={this.props.onCancel}>Cancel</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default PreOpSurvey;